import { MessageDescriptor } from 'react-intl';

export const TRAILING_DAYS = 'Trailing 7 days';

export interface DisplaySubMetricsProps {
  metrics: LighthouseMetrics;
  vertical?: string;
  cscEntityPrefix?: string;
  skill?: string;
  channel?: string;
  segment?: string;
  marketplace?: string;
  timeOption?: TimeOption;
  timestamp?: string;
}

export interface TypeMessageMap {
  RAP: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
    submetric: {
      totalHMDResponses: MessageDescriptor;
      hmdYesAdjusted: MessageDescriptor;
      hmdYesResponses: MessageDescriptor;
    };
  };
  CCX: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  CCX_V2: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  CCX_NEG: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  CCX_NEG_ONEBOX: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  TR: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  DART: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  DCR: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  ACHT: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  DSR: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  SDR: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  CONSULT_RATE: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  POLICY_ADHERENCE: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  PRRNC: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  PRRCI: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  CONCESSION_RATE: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  UNDERSTANDABILITY: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  UNDERSTANDABILITY_NO_CONCESSION: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  UNDERSTANDABILITY_CONCESSION_ISSUED: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  PRR: {
    titleMessage: MessageDescriptor;
  };
  HMD_OFFER_RATE: {
    titleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  HMD_RESPONSE_RATE: {
    titleMessage: MessageDescriptor;
  };
  QCS: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
  QCS_RAP: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
    submetric: {
      totalHMDResponses: MessageDescriptor;
      hmdYesAdjusted: MessageDescriptor;
      hmdYesResponses: MessageDescriptor;
    };
  };
  FCR: {
    titleMessage: MessageDescriptor;
    subTitleMessage: MessageDescriptor;
    popoverMessage: {
      definition: MessageDescriptor;
      formula: MessageDescriptor;
    };
  };
}

export interface ButtonPopoverProps {
  type: string;
}

export type GeneralMetricType =
  | RAPData
  | CCXData
  | CCXNegData
  | DARTData
  | DCRData
  | ACHTData
  | TRData
  | DSRData
  | SDRData
  | ConsultRateData
  | PolicyAdherenceData
  | PRRNoConcessionData
  | PRRConcessionIssuedData
  | ConcessionRateData
  | UnderstanabilityData
  | PRRData
  | HMDResponseRateData
  | UnderstandabilityNoConcessionData
  | UnderstandabilityConcessionIssuedData
  | HMDOfferRateData
  | QCSData
  | FCRData;

export type DetailSubMetricType =
  | RAPSubmetrics
  | CCXSubMetrics
  | DARTSubMetrics
  | DCRSubMetrics
  | ACHTSubMetrics
  | TransferRateSubMetrics
  | DSRSubMetrics
  | SDRSubMetrics
  | ConsultRateSubMetrics
  | PolicyAdherenceSubmetrics
  | PRRConcessionIssuedDataSubmetrics
  | PRRNoConcessionDataSubmetrics
  | ConcessionRateSubMetrics
  | UnderstanabilitySubMetrics
  | PRRSubMetrics
  | HMDResponseRateSubMetrics
  | HMDOfferRateSubMetrics;

export interface BaseMetric {
  datapoint: string | null;
  networkAverage: string | null;
}

export interface TopLevelMetricData {
  baseMetric: BaseMetric;
  change?: string;
  previousDate?: string;
}

export interface TrendDatapoint {
  timestamp: string;
  datapoint: string | null;
  networkAverage: string | null;
}

export interface RAPData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: RAPSubmetrics;
  trend?: TrendDatapoint[];
}

export interface RAPSubmetrics {
  totalHmdResponses?: BaseMetric;
  hmdYesAdjustedCount?: BaseMetric;
  prr?: BaseMetric;
  rapRepeatCount?: BaseMetric;
  hmdOfferRate?: BaseMetric;
  hmdResponseRate?: BaseMetric;
  hmdYesResponses?: BaseMetric;
  hmdNoResponses?: BaseMetric;
  fcr?: BaseMetric;
  nonOrderTaggingRate?: BaseMetric;
}

export interface CCXData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: CCXSubMetrics;
  trend?: TrendDatapoint[];
}

export interface CCXSubMetrics {
  understandability?: BaseMetric;
  attentive?: BaseMetric;
  friendliness?: BaseMetric;
  knowledgeable?: BaseMetric;
  // SubMetrics only for V2
  empathy?: BaseMetric;
  courtesy?: BaseMetric;
  helpfulness?: BaseMetric;
  efficiency?: BaseMetric;
}

export interface CCXNegData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: CCXNegSubMetrics;
  trend?: TrendDatapoint[];
}

// CCXNegSubMetrics are only for V2 Verticals
export interface CCXNegSubMetrics {
  negativeEmpathy?: BaseMetric;
  negativeCourtesy?: BaseMetric;
  negativeUnderstandability?: BaseMetric;
  negativeKnowledgeable?: BaseMetric;
  negativeHelpfulness?: BaseMetric;
  negativeEfficiency?: BaseMetric;
}

export interface CCXNegOneboxData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: CCXNegOneboxSubMetrics;
  trend?: TrendDatapoint[];
}

export interface CCXNegOneboxSubMetrics {
  negativeOneboxEmpathy?: BaseMetric;
  negativeOneboxCourtesy?: BaseMetric;
  negativeOneboxUnderstandability?: BaseMetric;
  negativeOneboxKnowledgeable?: BaseMetric;
  negativeOneboxHelpfulness?: BaseMetric;
  negativeOneboxEfficiency?: BaseMetric;
}

export interface UnderstandabilityData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: UnderstandabilitySubMetrics;
  trend?: TrendDatapoint[];
}

export interface UnderstandabilitySubMetrics {
  ccxOverallService?: BaseMetric;
  attentive?: BaseMetric;
  friendliness?: BaseMetric;
  knowledgeable?: BaseMetric;
}

export interface UnderstandabilityNoConcessionData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: undefined;
  trend?: TrendDatapoint[];
}

export interface UnderstandabilityConcessionIssuedData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: undefined;
  trend?: TrendDatapoint[];
}

export interface TRData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: TransferRateSubMetrics;
  trend?: TrendDatapoint[];
}

export interface TransferRateSubMetrics {
  transferredContacts?: BaseMetric;
  muParkedRate?: BaseMetric;
  handledContacts?: BaseMetric;
  boomerangContactCount?: BaseMetric;
  consults?: BaseMetric;
  resolvedContacts?: BaseMetric;
  escalatedContacts?: BaseMetric;
  lockToResolve?: BaseMetric;
  resolveRate?: BaseMetric;
}

export interface ACHTData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: ACHTSubMetrics;
  trend?: TrendDatapoint[];
}

export interface ACHTSubMetrics {
  acwTime?: BaseMetric;
  holdTime?: BaseMetric;
  ttcForMessageUs?: BaseMetric;
  acwTimeInbound?: BaseMetric;
  acwTimeOutbound?: BaseMetric;
  highSilenceRate?: BaseMetric;
}

export interface DARTData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: DARTSubMetrics;
  trend?: TrendDatapoint[];
}

export interface DARTSubMetrics {
  dartUsedCount?: BaseMetric;
  totalHandledContact?: BaseMetric;
  escalatedContacts?: BaseMetric;
}

export interface ConsultRateData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: ConsultRateSubMetrics;
  trend?: TrendDatapoint[];
}

export interface ConsultRateSubMetrics {
  consults?: BaseMetric;
  phoneHandledContacts?: BaseMetric;
}

export interface DCRData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: DCRSubMetrics;
  trend?: TrendDatapoint[];
}

export interface DCRSubMetrics {
  giftCardsIssuedCount?: BaseMetric;
  oowExceptions?: BaseMetric;
}

export interface DSRData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: DSRSubMetrics;
  trend?: TrendDatapoint[];
}

export interface DSRSubMetrics {
  dsrDenominator: BaseMetric;
  dsrYes: BaseMetric;
}

export interface SDRData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: SDRSubMetrics;
  trend?: TrendDatapoint[];
}

export interface SDRSubMetrics {
  sdrDenominator: BaseMetric;
  sdrYes: BaseMetric;
}

export interface PolicyAdherenceData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: PolicyAdherenceSubmetrics;
  trend?: TrendDatapoint[];
}

export interface PolicyAdherenceSubmetrics {
  concessionGivenRate?: BaseMetric;
}

export interface PRRNoConcessionData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: PRRNoConcessionDataSubmetrics;
  trend?: TrendDatapoint[];
}

export interface PRRConcessionIssuedData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: PRRConcessionIssuedDataSubmetrics;
  trend?: TrendDatapoint[];
}
export interface PRRNoConcessionDataSubmetrics {
  understandabilityNoConcession?: BaseMetric;
}
export interface PRRConcessionIssuedDataSubmetrics {
  understandabilityConcessionIssued?: BaseMetric;
}

export interface ConcessionRateData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: ConcessionRateSubMetrics;
  trend?: TrendDatapoint[];
}

export interface ConcessionRateSubMetrics {
  promoteGCIssuedCount?: BaseMetric;
  oopExceptions?: BaseMetric;
  concessionIssuedPerContact?: BaseMetric;
}

export interface UnderstanabilityData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: UnderstanabilitySubMetrics;
  trend?: TrendDatapoint[];
}

export interface UnderstanabilitySubMetrics {
  ccxOverallService?: BaseMetric;
  attentive?: BaseMetric;
  friendliness?: BaseMetric;
  knowledgeable?: BaseMetric;
}

export interface PRRData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: PRRSubMetrics;
  trend?: TrendDatapoint[];
}

export interface PRRSubMetrics {
  hmdYesResponses?: BaseMetric;
  hmdNoResponses?: BaseMetric;
}

export interface HMDOfferRateData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: HMDOfferRateSubMetrics;
  trend?: TrendDatapoint[];
}

export interface HMDOfferRateSubMetrics {
  totalHandledContact?: BaseMetric;
}

export interface HMDResponseRateData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: HMDResponseRateSubMetrics;
  trend?: TrendDatapoint[];
}

export interface HMDResponseRateSubMetrics {
  totalHmdResponses?: BaseMetric;
  offerCount?: BaseMetric;
}

export interface QCSData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: QCSSubMetrics;
  trend?: TrendDatapoint[];
}

export interface QCSSubMetrics {
  CCX: CCXData;
  ACHT: ACHTData;
  TR: TRData;
  DART?: DARTData;
}

export interface FCRData {
  topLevelMetrics: TopLevelMetricData;
  submetrics?: undefined;
  trend?: TrendDatapoint[];
}

export interface LighthouseFilters {
  verticals: string[];
  skills: string[];
  channels: string[];
  segments: string[];
  // ToDo - Remove null when Marketplace is released
  marketplaces: string[] | null;
}

export interface LighthouseMetrics {
  userID: string;
  metricTypes: string[];
  latestMetricPublishDate: string;
  RAP?: RAPData;
  CCX?: CCXData;
  CCX_NEG?: CCXNegData | null;
  CCX_NEG_ONEBOX?: CCXNegOneboxData | null;
  TR?: TRData;
  ACHT?: ACHTData;
  DART?: DARTData;
  DCR?: DCRData;
  DSR?: DSRData;
  SDR?: SDRData;
  CONSULT_RATE?: ConsultRateData;
  POLICY_ADHERENCE?: PolicyAdherenceData;
  PRRNC?: PRRNoConcessionData;
  PRRCI?: PRRConcessionIssuedData;
  CONCESSION_RATE?: ConcessionRateData;
  UNDERSTANDABILITY?: UnderstanabilityData;
  UNDERSTANDABILITY_NO_CONCESSION?: UnderstandabilityNoConcessionData;
  UNDERSTANDABILITY_CONCESSION_ISSUED?: UnderstandabilityConcessionIssuedData;
  PRR?: PRRData;
  HMD_OFFER_RATE?: HMDOfferRateData;
  HMD_RESPONSE_RATE?: HMDResponseRateData;
  QCS?: QCSData;
  FCR?: FCRData;
}

export enum TimeOption {
  WEEKLY = 'WEEKLY',
  DAILY = 'DAILY',
}

export enum LighthouseMetricOption {
  RAP = 'RAP',
  CCX = 'CCX',
  TR = 'TR',
  ACHT = 'ACHT',
  DART = 'DART',
  DCR = 'DCR',
  DSR = 'DSR',
  SDR = 'SDR',
  CONSULT_RATE = 'CONSULT_RATE',
  POLICY_ADHERENCE = 'POLICY_ADHERENCE',
  PRRNC = 'PRRNC',
  PRRCI = 'PRRCI',
  CONCESSION_RATE = 'CONCESSION_RATE',
  UNDERSTANDABILITY = 'UNDERSTANDABILITY',
  UNDERSTANDABILITY_NO_CONCESSION = 'UNDERSTANDABILITY_NO_CONCESSION',
  UNDERSTANDABILITY_CONCESSION_ISSUED = 'UNDERSTANDABILITY_CONCESSION_ISSUED',
  PRR = 'PRR',
  HMD_OFFER_RATE = 'HMD_OFFER_RATE',
  HMD_RESPONSE_RATE = 'HMD_RESPONSE_RATE',
  QCS = 'QCS',
  FCR = 'FCR',
}
